import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Badge,
  useToast,
} from "@asm/ui";
import type { ReconConfig } from "@/lib/api";

interface ReconConfigurationProps {
  config: ReconConfig;
  onUpdate: (updates: Partial<ReconConfig>) => Promise<void>;
}

const ALL_MODULES = [
  "dns", "email", "whois", "tls", "headers", "cloud", "asn",
  "tech", "repos", "crawl_hints", "js_analysis", "favicon",
  "typosquat", "ipintel", "vuln",
];

export function ReconConfiguration({ config, onUpdate }: ReconConfigurationProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    modules: [...config.modules],
    max_domains_per_hour: config.max_domains_per_hour,
    scan_interval_hours: config.scan_interval_hours,
    min_idle_workers: config.min_idle_workers,
    retention_days: config.retention_days,
    rate_limit_dns_qps: config.rate_limit_dns_qps,
    rate_limit_http_qps: config.rate_limit_http_qps,
    rate_limit_whois_qps: config.rate_limit_whois_qps,
    rate_limit_github_qps: config.rate_limit_github_qps,
    rate_limit_external_api_qps: config.rate_limit_external_api_qps,
    target_tlds: config.target_tlds.join(", "),
    target_countries: config.target_countries.join(", "),
    campaign_only: config.campaign_only ?? true,
  });

  const toggleModule = (mod: string) => {
    setFormData((prev) => ({
      ...prev,
      modules: prev.modules.includes(mod)
        ? prev.modules.filter((m) => m !== mod)
        : [...prev.modules, mod],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate({
        modules: formData.modules,
        max_domains_per_hour: formData.max_domains_per_hour,
        scan_interval_hours: formData.scan_interval_hours,
        min_idle_workers: formData.min_idle_workers,
        retention_days: formData.retention_days,
        rate_limit_dns_qps: formData.rate_limit_dns_qps,
        rate_limit_http_qps: formData.rate_limit_http_qps,
        rate_limit_whois_qps: formData.rate_limit_whois_qps,
        rate_limit_github_qps: formData.rate_limit_github_qps,
        rate_limit_external_api_qps: formData.rate_limit_external_api_qps,
        target_tlds: formData.target_tlds
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        target_countries: formData.target_countries
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
        campaign_only: formData.campaign_only,
      });
      toast({
        title: "Configuration Updated",
        description: "Recon settings have been saved.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to save configuration",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Campaign-Only Mode */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Campaign-Only Mode</h3>
              <p className="text-sm text-muted-foreground">
                Only scan domains assigned to active campaigns
              </p>
            </div>
            <Button
              variant={formData.campaign_only ? "default" : "outline"}
              size="sm"
              onClick={() =>
                setFormData((prev) => ({ ...prev, campaign_only: !prev.campaign_only }))
              }
            >
              {formData.campaign_only ? "Enabled" : "Disabled"}
            </Button>
          </div>
          {!formData.campaign_only && (
            <p className="text-xs text-amber-500 mt-2">
              Domains not assigned to any campaign will also be scanned (global pool).
            </p>
          )}
        </CardContent>
      </Card>

      {/* Modules */}
      <Card>
        <CardHeader>
          <CardTitle>Scan Modules</CardTitle>
          <CardDescription>
            Select which reconnaissance modules to run during scans
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {ALL_MODULES.map((mod) => {
              const isActive = formData.modules.includes(mod);
              return (
                <Badge
                  key={mod}
                  variant={isActive ? "default" : "outline"}
                  className="cursor-pointer select-none"
                  onClick={() => toggleModule(mod)}
                >
                  {mod}
                </Badge>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Click to toggle modules on/off
          </p>
        </CardContent>
      </Card>

      {/* Rate Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Rate Limits</CardTitle>
          <CardDescription>
            Control scanning speed to avoid overwhelming targets or external services
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="max_domains_per_hour">Max Domains/Hour</Label>
              <Input
                id="max_domains_per_hour"
                type="number"
                value={formData.max_domains_per_hour}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    max_domains_per_hour: parseInt(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate_limit_dns_qps">DNS QPS</Label>
              <Input
                id="rate_limit_dns_qps"
                type="number"
                step="0.1"
                value={formData.rate_limit_dns_qps}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    rate_limit_dns_qps: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate_limit_http_qps">HTTP QPS</Label>
              <Input
                id="rate_limit_http_qps"
                type="number"
                step="0.1"
                value={formData.rate_limit_http_qps}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    rate_limit_http_qps: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate_limit_whois_qps">WHOIS QPS</Label>
              <Input
                id="rate_limit_whois_qps"
                type="number"
                step="0.1"
                value={formData.rate_limit_whois_qps}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    rate_limit_whois_qps: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate_limit_github_qps">GitHub API QPS</Label>
              <Input
                id="rate_limit_github_qps"
                type="number"
                step="0.1"
                value={formData.rate_limit_github_qps}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    rate_limit_github_qps: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate_limit_external_api_qps">External API QPS</Label>
              <Input
                id="rate_limit_external_api_qps"
                type="number"
                step="0.1"
                value={formData.rate_limit_external_api_qps}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    rate_limit_external_api_qps: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Targeting */}
      <Card>
        <CardHeader>
          <CardTitle>Targeting</CardTitle>
          <CardDescription>
            Filter which domains to scan based on TLD or country
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="target_tlds">Target TLDs</Label>
              <Input
                id="target_tlds"
                value={formData.target_tlds}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, target_tlds: e.target.value }))
                }
                placeholder="com, io, net, org"
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list. Leave empty for all TLDs.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="target_countries">Target Countries</Label>
              <Input
                id="target_countries"
                value={formData.target_countries}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, target_countries: e.target.value }))
                }
                placeholder="US, GB, DE"
              />
              <p className="text-xs text-muted-foreground">
                ISO country codes. Leave empty for all countries.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduling */}
      <Card>
        <CardHeader>
          <CardTitle>Scheduling</CardTitle>
          <CardDescription>
            Control how often domains are re-scanned and data retention
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="scan_interval_hours">Re-scan Interval (hours)</Label>
              <Input
                id="scan_interval_hours"
                type="number"
                value={formData.scan_interval_hours}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    scan_interval_hours: parseInt(e.target.value) || 0,
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                How often to re-scan domains (168 = 7 days)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="min_idle_workers">Min Idle Workers</Label>
              <Input
                id="min_idle_workers"
                type="number"
                value={formData.min_idle_workers}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    min_idle_workers: parseInt(e.target.value) || 0,
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Start recon when this many workers are idle
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="retention_days">Result Retention (days)</Label>
              <Input
                id="retention_days"
                type="number"
                value={formData.retention_days}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    retention_days: parseInt(e.target.value) || 0,
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                How long to keep scan results
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>
    </div>
  );
}
